import axios from "axios";
import type { QueryClient } from "@tanstack/react-query";
import { scopedKey, type QueryScope } from "./scope";

/**
 * What a confirmed write does to the cache (contract §3). Each writer names,
 * per key, whether it **invalidates** (refetch on the next observe, or now if
 * observed), **patches** (writes the response into an entry that already
 * exists, no request), or leaves it alone. Every helper here takes a scope, so
 * a writer can only reach the signed-in principal's entries in one workspace.
 */

/**
 * Invalidate every entry under `[tenant, principal, domain, …prefix]`. Prefix
 * match: `("project", id)` covers the detail and all its sub-resources. Settles
 * when the observed ones have refetched; a failed refetch does not reject it
 * (the cache logs it, `client.ts`).
 */
export function invalidateScoped(
  client: QueryClient,
  scope: QueryScope,
  domain: string,
  ...prefix: readonly unknown[]
): Promise<void> {
  return client.invalidateQueries({ queryKey: scopedKey(scope, domain, ...prefix) });
}

/**
 * Write a response into the one entry at `key` if it is already cached. An
 * absent entry stays absent: a write's response is not the read's full
 * representation, so it must not seed a fresh-looking entry.
 */
export function patchScoped<T>(
  client: QueryClient,
  key: readonly unknown[],
  update: (current: T) => T,
): void {
  client.setQueryData<T>(key, (current) => (current === undefined ? undefined : update(current)));
}

/**
 * On a resource's 403 or 404, remove everything cached under the prefix so a
 * later mount cannot render the old answer, and report whether it did. Any
 * other error leaves the cache alone. This is the resource's own denial; a
 * dead session is `syncQueryPrincipal`'s job.
 */
export function dropOnDenial(
  client: QueryClient,
  scope: QueryScope,
  error: unknown,
  domain: string,
  ...prefix: readonly unknown[]
): boolean {
  const status = axios.isAxiosError(error) ? error.response?.status : undefined;
  if (status !== 403 && status !== 404) return false;
  client.removeQueries({ queryKey: scopedKey(scope, domain, ...prefix) });
  return true;
}
